import { Grid, SxProps, ToggleButton, Typography, Theme, Box } from '@mui/material';
import { DetailedRound } from '../models/round';
import { Syndicate } from '../models/syndicate';
import { IsLeo, IsRightToLeftTheme, themeC } from '../theme/themehelper';
import { useTranslation } from 'react-i18next';
import { getTeamName } from '../slimcoupon/couponhelpers';

const SyndicateCoupon = ({ syndicate, round, theme }: { syndicate: Syndicate, round: DetailedRound, theme: string }) => {

  const { t } = useTranslation();

  if (round === undefined) {
    return (<></>);
  }

  let button: SxProps<Theme> = {
    padding: "0px 5px",
    textAlign: "center",
    height: "40px",
    fontFamily: "var(--font-body)",
    minWidth: "40px",
    maxWidth: "40px",
    marginRight: "10px",
    border: 'var(--sign-border-width) solid var(--sign-border-color, var(--off-black))',
    borderRadius: 'var(--sign-border-radius)',
    background: 'var(--sign-background-not-selected)',
    color: 'var(--sign-color)',
    "&.Mui-selected": {
      color: 'var(--sign-color-selected)',
      backgroundColor: 'var(--sign-background)',
      borderColor: 'var(--sign-border-color-selected, var(--sign-border-color, var(--off-black)))'
    }
  }

  const isAfterDeadline = new Date(round.openTo) < new Date(Date.now());

  const lastButton: SxProps<Theme> = {
    marginRight: "0px",
  }

  let signText: SxProps<Theme> = {
    fontSize: "13px",
    lineHeight: "18px",
    fontFamily: "var(--font-body)"
  }

  let isRightToLeft = IsRightToLeftTheme(theme);

  let teams: SxProps<Theme> = {
    fontSize: "13px",
    lineHeight: "19px",
    fontWeight: "500",
    fontFamily: "var(--font-body)",
    textAlign: isRightToLeft ? "right" : ""
  }

  let gameNr: SxProps<Theme> = {
    width: "20px",
    marginRight: "5px"
  }

  const styleRow: SxProps<Theme> = {
    color: "var(--text-black)",
    padding: "10px",
    borderBottom: "1px solid var(--off-black)"
  };

  let isReduced = false;
  let isSingleRows = false;

  if (!syndicate.couponHidden || syndicate.showDraftCoupon) {
    isReduced = syndicate?.pendingRoundCoupon?.bets?.[0]?.system?.type === "REDUCED";
    isSingleRows = (syndicate?.pendingRoundCoupon?.bets?.length ?? 0) > 1;
  }

  type ScoreObject = {
    "1": number;
    "X": number;
    "2": number;
  };
  let scores: ScoreObject[] = [];

  if (isSingleRows) {
    for (let game = 0; game < round.fixtures.length; game++) {
      scores.push({ "1": 0, "X": 0, "2": 0 })
    }

    const pendingBets = syndicate?.pendingRoundCoupon?.bets ?? [];
    for (let index = 0; index < pendingBets.length; index++) {
      const bet = pendingBets[index] ?? { legs: [] as any[] };
      for (let game = 0; game < (bet.legs?.length ?? 0); game++) {
        let picks = bet.legs?.[game]?.signs ?? [];
        let score = scores[game];
        if (picks.indexOf("ONE") > -1) {
          score["1"]++;
        }
        if (picks.indexOf("X") > -1) {
          score["X"]++;
        }
        if (picks.indexOf("TWO") > -1) {
          score["2"]++;
        }

      }
    }
  }

  function isSelected(index: number, sign: string): boolean {
    if (!syndicate?.isFinal && !syndicate?.showDraftCoupon)
      return false;
    if (syndicate?.pendingRoundCoupon.bets?.length > 1)
      return false;
    if (isReduced)
      return false;

    const legs = syndicate?.pendingRoundCoupon?.bets?.[0]?.legs;
    const leg = legs?.[index];
    const signs = leg?.signs;

    return signs?.includes(sign) ?? false;

  }

  let correct = {
    borderColor: 'var(--sign-correct-border)',
    borderWidth: "3px"
  }

  function getSignStyle(round: DetailedRound, index: number, sign: string): SxProps<Theme> {

    let draft: SxProps<Theme> = {};

    if (!syndicate.isFinal && syndicate.showDraftCoupon) {
      draft = {
        "&.Mui-selected": {
          color: 'var(--button-disabled-text-color)',
          backgroundColor: 'var(--button-disabled)',
        }
      }
    }

    if (isReduced) {

      let summary = 0;
      const leg = syndicate?.pendingRoundCoupon?.bets?.[0]?.legs?.[index];
      const signs = leg?.signs ?? [];
      const found = signs.indexOf(sign) > -1;
      const percent = signs.length > 0 ? 1 / signs.length : 0;
      summary = found ? percent : 0;

      let partial: SxProps<Theme> = {
        backgroundImage: 'linear-gradient(to top, var(--sign-background) 0 ' + summary * 100 + '%, var(--sign-background-not-selected) ' + summary * 100 + '% 100%)',
        color: summary > 0.5 ? 'var(--sign-color-selected)' : 'var(--sign-color)'
      }

      if (isAfterDeadline && round.preliminaryCorrectRow && round.preliminaryCorrectRow[index] === sign)
        return { ...button, ...partial, ...correct } as SxProps<Theme>

      return { ...button, ...partial, ...draft } as SxProps<Theme>
    }

    if (isSingleRows) {

      let summary = 0;
      switch (sign) {
        case "ONE":
          summary = scores[index]["1"] / (scores[index]["1"] + scores[index]["X"] + scores[index]["2"])
          break;
        case "X":
          summary = scores[index]["X"] / (scores[index]["1"] + scores[index]["X"] + scores[index]["2"])
          break;
        case "TWO":
          summary = scores[index]["2"] / (scores[index]["1"] + scores[index]["X"] + scores[index]["2"])
          break;
      }
      let partial: SxProps<Theme> = {
        backgroundImage: 'linear-gradient(to top, var(--sign-background) 0 ' + summary * 100 + '%, var(--sign-background-not-selected) ' + summary * 100 + '% 100%)',
        color: summary > 0.5 ? 'var(--sign-color-selected)' : 'var(--sign-color)'
      }

      if (isAfterDeadline && round.preliminaryCorrectRow && round.preliminaryCorrectRow[index] === sign)
        return { ...button, ...partial, ...correct } as SxProps<Theme>

      return { ...button, ...partial, ...draft } as SxProps<Theme>
    }

    if (isAfterDeadline && round.preliminaryCorrectRow && round.preliminaryCorrectRow[index] === sign)
      return { ...button, ...correct } as SxProps<Theme>

    return { ...button, ...draft } as SxProps<Theme>;
  }

  if (IsLeo(theme)) {
    signText.fontWeight = "bold";
  }

  if ((theme === themeC.Kung)) {
    signText = {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      fontFamily: "var(--font-body)"
    }
    button = { ...button, ...{ border: '1px solid #249CEC' } };
  }

  if ((theme === themeC.Dbet)) {
    signText = {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      fontFamily: "var(--font-body)"
    }

    teams = {
      fontSize: "14px",
      lineHeight: "16.41px",
      fontWeight: "700",
      fontFamily: "var(--font-body)",
      textAlign: isRightToLeft ? "right" : ""
    }
    gameNr.color = "#5E5E5E";
    button = { ...button, ...{ border: '1px solid #249CEC' } };
  };

  if ((theme === themeC.Video)) {
    signText = {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      fontFamily: "var(--font-body)"
    }

    teams = {
      fontSize: "14px",
      lineHeight: "16.41px",
      fontWeight: "700",
      fontFamily: "var(--font-body)",
      textAlign: isRightToLeft ? "right" : ""
    }
    gameNr.color = "#5E5E5E";
    button = { ...button, ...{ border: '1px solid #1D1D1D' } };
  };

  if ((theme === themeC.MrVegas)) {
    signText = {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      fontFamily: "var(--font-body)"
    }

    teams = {
      fontSize: "14px",
      lineHeight: "16.41px",
      fontWeight: "700",
      fontFamily: "var(--font-body)",
      textAlign: isRightToLeft ? "right" : ""
    }
    gameNr.color = "#5E5E5E";
    button = { ...button, ...{ border: '1px solid #1D1D1D' } };
  };

  if ((theme === themeC.Mega)) {
    signText = {
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      fontFamily: "var(--font-body)"
    }

    teams = {
      fontSize: "14px",
      lineHeight: "16.41px",
      fontWeight: "700",
      fontFamily: "var(--font-body)",
      textAlign: isRightToLeft ? "right" : ""
    }
    gameNr.color = "#5E5E5E";
    button = { ...button, ...{ border: '1px solid #1D1D1D' } };
  };

  return (
    <Grid container sx={{ justifyContent: "center", marginTop: "0px", marginBottom: "0px" }}>
      <Grid container direction="column"
        sx={{ width: "100%", maxWidth: "500px", background: 'var(--background_2)', margin: "0px" }}>

        {!syndicate.isFinal &&
          <Grid container justifyContent="center">
            <Typography style={{ fontWeight: 'bold', textAlign: 'center', fontSize: "16px", marginBottom: "20px", maxWidth: "250px" }}>
              {t('syndicate_not_finalised')}
            </Typography>
          </Grid>
        }

        {round.fixtures.map((fixture, i) => {

          return (
            <Grid key={"row_header" + i} sx={styleRow}>
              <Grid container key={"row_" + i}
                sx={{ flexDirection: "var(--flex-direction)", flexWrap: "nowrap" }}
                alignItems="stretch">
                <Grid item>
                  <Typography
                    sx={{ ...teams, ...gameNr } as SxProps<Theme>}>{i + 1}</Typography>
                </Grid>
                <Grid item sx={{ flexGrow: 1, paddingRight: "5px" }}>
                  <Grid container sx={{ flexDirection: "var(--flex-direction)" }}>
                    <Typography sx={teams}>{getTeamName(fixture.homeTeam, t)}</Typography>
                  </Grid>
                  <Grid container sx={{ flexDirection: "var(--flex-direction)" }}>
                    <Typography sx={teams}>{getTeamName(fixture.awayTeam, t)}</Typography>
                  </Grid>

                </Grid>

                {(syndicate.couponHidden && !syndicate.showDraftCoupon) ?


                  <Grid item sx={{ minHeight: '100%' }}>
                    <Grid container alignItems="center" sx={{
                      minHeight: '100%',
                      flexDirection: "var(--flex-direction)",
                      flexWrap: "nowrap"
                    }}>
                      <Box sx={{ width: "40px" }}>
                        <Typography sx={signText}>1</Typography>
                      </Box>
                      <Box sx={{ width: "40px" }}>
                        <Typography sx={signText}>X</Typography>
                      </Box>
                      <Box sx={{ width: "40px" }}>
                        <Typography sx={signText}>2</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  :

                  <Grid item sx={{ minHeight: '100%' }}>
                    <Grid container alignItems="center" sx={{
                      minHeight: '100%',
                      flexDirection: "var(--flex-direction)",
                      flexWrap: "nowrap"
                    }}>
                      <ToggleButton name="1"
                        selected={isSelected(i, "ONE")}
                        value="1" sx={getSignStyle(round, i, "ONE")}>
                        <Typography sx={signText}>1</Typography>
                      </ToggleButton>
                      <ToggleButton name="X"
                        selected={isSelected(i, "X")}
                        value="X" sx={getSignStyle(round, i, "X")}>
                        <Typography sx={signText}>X</Typography>
                      </ToggleButton>
                      <ToggleButton name="2"
                        selected={isSelected(i, "TWO")}
                        value="2" sx={
                          isRightToLeft ? getSignStyle(round, i, "TWO") : { ...getSignStyle(round, i, "TWO"), ...lastButton } as SxProps<Theme>}>
                        <Typography sx={signText}>2</Typography>
                      </ToggleButton>
                    </Grid>
                  </Grid>
                }

              </Grid>
            </Grid>
          )
        }

        )}
      </Grid>
    </Grid>
  );
}
export default SyndicateCoupon;